import { Alert, Box, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppCustomStyles } from "../../../assets/theme/custom/hooks";
import { AppSlideOver } from "../../../components/layout";
import type { IngestJobResponse } from "../../../services/ingestService";

type Props = {
  open: boolean;
  job: IngestJobResponse | null;
  loading?: boolean;
  onClose: () => void;
};

function statusColor(status?: string): "default" | "success" | "warning" | "error" | "info" {
  switch (String(status ?? "").toUpperCase()) {
    case "COMPLETED":
      return "success";
    case "PARTIALLY_COMPLETED":
      return "warning";
    case "FAILED":
      return "error";
    case "PROCESSING":
    case "PENDING":
      return "info";
    default:
      return "default";
  }
}

export default function IngestJobDetailPanel({ open, job, loading = false, onClose }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();

  return (
    <AppSlideOver open={open} onClose={onClose} size="xl" title={t("ingest.jobDetailTitle")}>
      {job && (
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={job.status} color={statusColor(job.status)} />
          {job.progress && (job.status === "PROCESSING" || job.status === "PENDING") ? (
            <Typography variant="body2" color="text.secondary">
              {t(`ingest.progress.${job.progress}`, { defaultValue: job.progress })}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary">
            {t("ingest.jobDetailStats", {
              indexed: job.indexedCount,
              failed: job.errorCount,
              total: job.rowCount,
            })}
          </Typography>
        </Stack>
      )}

      <Box sx={custom.components.dataPrep.runDetailBody}>
        {loading && (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" mt={1}>
              {t("ingest.jobDetailLoading")}
            </Typography>
          </Stack>
        )}

        {!loading && job && (
          <Stack spacing={2}>
            <Stack spacing={0.75}>
              <DetailRow label={t("ingest.colFile")} value={job.sourceFileName || "—"} />
              <DetailRow label={t("ingest.colIndex")} value={job.targetIndex} />
              <DetailRow label={t("ingest.colEnv")} value={job.environment} />
              {job.bulkBatchSize ? (
                <DetailRow label={t("ingest.bulkBatchSize")} value={String(job.bulkBatchSize)} />
              ) : null}
            </Stack>

            {job.errors && job.errors.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="subtitle2">{t("ingest.jobDetailErrors")}</Typography>
                {job.errors.map((message, index) => (
                  <Alert key={index} severity="error" sx={custom.pages.shared.alertCompactPyQuarter}>
                    {message}
                  </Alert>
                ))}
              </Stack>
            )}

            {job.failedRecords && job.failedRecords.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={custom.pages.shared.sectionHeaderRow}>
                  {t("ingest.failureReportTitle", { count: job.errorCount })}
                </Typography>
                <Box sx={custom.pages.shared.overflowAuto}>
                  <Table size="small" sx={custom.pages.shared.tableMinWidth720}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("ingest.colFailureRow")}</TableCell>
                        <TableCell>{t("ingest.colFailureColumn")}</TableCell>
                        <TableCell>{t("ingest.colFailureType")}</TableCell>
                        <TableCell>{t("ingest.colFailureReason")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {job.failedRecords.map((failure, index) => (
                        <TableRow key={`${failure.rowNumber}-${failure.column ?? index}`}>
                          <TableCell>{failure.rowNumber}</TableCell>
                          <TableCell>{failure.column || "—"}</TableCell>
                          <TableCell>{failure.errorType || "—"}</TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>
                            {failure.errorReason || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {job.errorCount > job.failedRecords.length && (
                  <Typography variant="caption" color="text.secondary" sx={custom.pages.shared.captionBlockLineHeight}>
                    {t("ingest.failureReportTruncated", {
                      shown: job.failedRecords.length,
                      total: job.errorCount,
                    })}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </AppSlideOver>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Stack>
  );
}
